import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  DwConfigService,
  DwConfigServiceToken,
  DwMetaDataFormApi
} from '@devwareapps/devware-cap';
import { combineLatest, Observable, of, timer } from 'rxjs';
import { catchError, debounceTime, finalize, map, publishReplay, refCount, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import {
  AssignmentCourseLessonEntity,
  AssignmentEntity,
  AssignmentStudentEntity
} from '../../../meta-data/app-meta-data.service';
import {
  AssignmentResetImpact,
  AssignmentResetLessonImpact,
  AssignmentResetModuleImpact,
  AssignmentResetStudentImpact
} from '../models/assignment-reset-impact.model';
import { AssignmentRecipientOptionsService } from './assignment-recipient-options.service';

interface AssignmentResetImpactRequest {
  readonly AssignmentId: number | null;
  readonly CourseId: number;
  readonly CourseLessonIds: number[];
  readonly StudentIds: number[];
  readonly FlightSchoolGroupId: number;
  readonly UseGroupAssignment: boolean;
  readonly ResetLessonProgress: boolean;
}

interface AssignmentResetImpactCache {
  readonly impacts: Map<string, AssignmentResetImpact>;
  readonly inFlight: Map<string, Observable<AssignmentResetImpact>>;
  readonly lessonImpacts: Map<number, AssignmentResetLessonImpact>;
  readonly loadedLessonIds: Set<number>;
  summaryImpact: AssignmentResetImpact;
  summaryStudentIds: number[];
  activeStudentIds: number[];
  courseId: number;
  studentKey: string;
}

@Injectable({ providedIn: 'root' })
export class AssignmentResetImpactService {
  private readonly detailDebounceMs = 1000;
  private readonly summaryApiUrl: string;
  private readonly detailsApiUrl: string;
  private readonly formStreams = new WeakMap<object, Observable<AssignmentResetImpact>>();

  constructor(
    @Inject(DwConfigServiceToken) configService: DwConfigService,
    private http: HttpClient,
    private assignmentRecipientOptionsService: AssignmentRecipientOptionsService
  ) {
    const apiUrl = `${configService.coreConfig.apiRoot}/aviator-online/students/assignments/reset-impact`;
    this.summaryApiUrl = `${apiUrl}/summary`;
    this.detailsApiUrl = `${apiUrl}/details`;
  }

  observe(formApi: DwMetaDataFormApi): Observable<AssignmentResetImpact> {
    if (!formApi) {
      return of(this.emptyImpact());
    }

    const existingStream = this.formStreams.get(formApi as object);
    if (existingStream) {
      return existingStream;
    }

    const cache: AssignmentResetImpactCache = {
      impacts: new Map<string, AssignmentResetImpact>(),
      inFlight: new Map<string, Observable<AssignmentResetImpact>>(),
      lessonImpacts: new Map<number, AssignmentResetLessonImpact>(),
      loadedLessonIds: new Set<number>(),
      summaryImpact: null,
      summaryStudentIds: [],
      activeStudentIds: [],
      courseId: 0,
      studentKey: ''
    };

    const request$ = formApi.getFormDataChanges<AssignmentEntity>()
      .pipe(
        startWith(formApi.getFormData<AssignmentEntity>()),
        debounceTime(0),
        map(() => this.createRequest(formApi.getFormData<AssignmentEntity>())),
        shareReplay(1)
      );

    const loadedImpact$ = request$.pipe(
      switchMap(request => this.resolveRequestStudents(request, cache)),
      switchMap(request => this.loadImpact(request, cache)
        .pipe(map(impact => ({ request, impact })))),
      startWith({ request: null as AssignmentResetImpactRequest, impact: this.loadingImpact() }),
      shareReplay(1)
    );

    const stream = loadedImpact$.pipe(
      map(loaded => loaded.impact),
      shareReplay(1)
    );

    this.formStreams.set(formApi as object, stream);
    return stream;
  }

  private createRequest(assignment: AssignmentEntity): AssignmentResetImpactRequest {
    return {
      AssignmentId: Number(assignment?.AssignmentId || 0) || null,
      CourseId: Number(assignment?.CourseId || 0),
      CourseLessonIds: ((assignment?.AssignmentCourseLesson || []) as AssignmentCourseLessonEntity[])
        .filter(lesson => lesson._isDeleted !== true && !!lesson.CourseLessonId)
        .map(lesson => Number(lesson.CourseLessonId))
        .filter((lessonId, index, lessonIds) => lessonIds.indexOf(lessonId) === index)
        .sort((first, second) => first - second),
      StudentIds: ((assignment?.AssignmentStudent || []) as AssignmentStudentEntity[])
        .filter(student => student._isDeleted !== true && !!student.StudentId)
        .map(student => Number(student.StudentId))
        .filter((studentId, index, studentIds) => studentIds.indexOf(studentId) === index)
        .sort((first, second) => first - second),
      FlightSchoolGroupId: Number(assignment?.FlightSchoolGroupId || 0),
      UseGroupAssignment: assignment?.UseGroupAssignment === true,
      ResetLessonProgress: assignment?.ResetLessonProgress === true
    };
  }

  private resolveRequestStudents(
    request: AssignmentResetImpactRequest,
    cache: AssignmentResetImpactCache
  ): Observable<AssignmentResetImpactRequest> {
    if (cache.courseId !== request.CourseId) {
      cache.courseId = request.CourseId;
      cache.activeStudentIds = [];
      cache.lessonImpacts.clear();
      cache.loadedLessonIds.clear();
      cache.summaryImpact = null;
      cache.summaryStudentIds = [];
      cache.studentKey = '';
    }

    if (request.StudentIds.length > 0 || !request.UseGroupAssignment) {
      const incomingStudentIds = request.StudentIds || [];
      cache.activeStudentIds = incomingStudentIds;
      return of({ ...request, StudentIds: cache.activeStudentIds, UseGroupAssignment: false });
    }

    if (!request.FlightSchoolGroupId) {
      cache.activeStudentIds = [];
      return of({ ...request, StudentIds: [] });
    }

    return this.assignmentRecipientOptionsService.getOptions().pipe(map(options => {
      const group = (options.Groups || []).find(item => item.FlightSchoolGroupId === request.FlightSchoolGroupId);
      cache.activeStudentIds = (group?.StudentIds || []).slice().sort((first, second) => first - second);
      return { ...request, StudentIds: cache.activeStudentIds, UseGroupAssignment: false };
    }));
  }

  private loadImpact(
    request: AssignmentResetImpactRequest,
    cache: AssignmentResetImpactCache
  ): Observable<AssignmentResetImpact> {
    if (!request.CourseId || request.StudentIds.length === 0) {
      if (request.StudentIds.length === 0) {
        cache.summaryImpact = null;
        cache.summaryStudentIds = [];
        cache.lessonImpacts.clear();
        cache.loadedLessonIds.clear();
        cache.studentKey = '';
      }
      return of(this.emptyImpact());
    }

    const studentKey = request.StudentIds.slice().sort((first, second) => first - second).join(',');
    if (cache.studentKey !== studentKey) {
      cache.studentKey = studentKey;
      cache.lessonImpacts.clear();
      cache.loadedLessonIds.clear();
    }

    const summaryRequest: AssignmentResetImpactRequest = {
      ...request,
      CourseLessonIds: [],
      StudentIds: request.StudentIds.slice().sort((first, second) => first - second),
      UseGroupAssignment: false,
      FlightSchoolGroupId: 0,
      ResetLessonProgress: false
    };
    const summary$ = this.loadSummary(summaryRequest, cache);
    if (request.CourseLessonIds.length === 0) {
      return summary$.pipe(map(summary => summary.IsLoading
        ? summary
        : { ...this.mergeImpact(summary, null, request), IsRefreshing: summary.IsRefreshing }));
    }

    const detailsRequest: AssignmentResetImpactRequest = {
      ...request,
      StudentIds: request.StudentIds.slice().sort((first, second) => first - second),
      CourseLessonIds: request.CourseLessonIds.slice().sort((first, second) => first - second),
      UseGroupAssignment: false,
      FlightSchoolGroupId: 0
    };
    const detailsKey = `details:${this.createCacheKey(request.CourseId, request.StudentIds)}:${detailsRequest.CourseLessonIds.join(',')}`;
    const details$ = this.loadEndpoint(this.detailsApiUrl, detailsRequest, detailsKey, cache, this.detailDebounceMs);
    return combineLatest([summary$, details$]).pipe(map(([summary, details]) => {
      if (summary.IsLoading) {
        return summary;
      }
      if (details.IsLoading) {
        const cachedDetails = this.createCachedDetails(request.CourseLessonIds, cache);
        const loadingLessonIds = request.CourseLessonIds.filter(lessonId => !cache.loadedLessonIds.has(lessonId));
        return { ...this.mergeImpact(summary, cachedDetails, request, loadingLessonIds), IsRefreshing: true };
      }
      request.CourseLessonIds.forEach(lessonId => cache.loadedLessonIds.add(lessonId));
      (details.Lessons || []).forEach(lesson => cache.lessonImpacts.set(lesson.CourseLessonId, lesson));
      return { ...this.mergeImpact(summary, details, request), IsRefreshing: summary.IsRefreshing };
    }));
  }

  private loadSummary(
    request: AssignmentResetImpactRequest,
    cache: AssignmentResetImpactCache
  ): Observable<AssignmentResetImpact> {
    const requestedStudentIds = request.StudentIds.slice().sort((first, second) => first - second);
    const previousStudentIds = cache.summaryStudentIds;
    const isAddition = !!cache.summaryImpact
      && previousStudentIds.every(studentId => requestedStudentIds.indexOf(studentId) >= 0);
    const studentIdsToLoad = isAddition
      ? requestedStudentIds.filter(studentId => previousStudentIds.indexOf(studentId) < 0)
      : requestedStudentIds;

    if (isAddition && studentIdsToLoad.length === 0) {
      return of(cache.summaryImpact);
    }

    if (!isAddition) {
      cache.summaryImpact = null;
      cache.summaryStudentIds = [];
    }

    const summaryRequest = { ...request, StudentIds: studentIdsToLoad };
    const summaryKey = `summary:${this.createCacheKey(request.CourseId, studentIdsToLoad)}`;
    return this.loadEndpoint(this.summaryApiUrl, summaryRequest, summaryKey, cache).pipe(map(impact => {
      if (impact.IsLoading && cache.summaryImpact) {
        return { ...cache.summaryImpact, IsRefreshing: true };
      }
      if (impact.IsLoading) {
        return impact;
      }

      cache.summaryImpact = isAddition
        ? this.mergeSummaries(cache.summaryImpact, impact)
        : impact;
      cache.summaryStudentIds = requestedStudentIds;
      return cache.summaryImpact;
    }));
  }

  private mergeSummaries(
    current: AssignmentResetImpact,
    added: AssignmentResetImpact
  ): AssignmentResetImpact {
    const addedLessons = new Map<number, AssignmentResetLessonImpact>();
    (added.Lessons || []).forEach(lesson => addedLessons.set(lesson.CourseLessonId, lesson));
    const currentLessonIds = new Set((current.Lessons || []).map(lesson => lesson.CourseLessonId));
    const lessons = (current.Lessons || []).map(lesson => {
      const addedLesson = addedLessons.get(lesson.CourseLessonId);
      return addedLesson ? {
        ...lesson,
        StudentCount: lesson.StudentCount + addedLesson.StudentCount,
        CompletedStudentCount: lesson.CompletedStudentCount + addedLesson.CompletedStudentCount,
        PassedQuizCount: lesson.PassedQuizCount + addedLesson.PassedQuizCount
      } : lesson;
    }).concat((added.Lessons || []).filter(lesson => !currentLessonIds.has(lesson.CourseLessonId)));

    return {
      ...current,
      IsLoading: false,
      IsRefreshing: false,
      IsAvailable: current.IsAvailable && added.IsAvailable,
      EvaluatedStudentCount: current.EvaluatedStudentCount + added.EvaluatedStudentCount,
      Lessons: lessons
    };
  }

  private loadEndpoint(
    apiUrl: string,
    request: AssignmentResetImpactRequest,
    cacheKey: string,
    cache: AssignmentResetImpactCache,
    debounceMs: number = 0
  ): Observable<AssignmentResetImpact> {
    const cachedImpact = cache.impacts.get(cacheKey);
    if (cachedImpact) {
      return of(cachedImpact);
    }

    const existingLoad = cache.inFlight.get(cacheKey);
    if (existingLoad) {
      return existingLoad;
    }

    const requestTrigger$ = debounceMs > 0 ? timer(debounceMs) : of(null);
    const load$ = requestTrigger$.pipe(
      switchMap(() => this.http.post<AssignmentResetImpact>(apiUrl, request)),
      map(impact => ({ ...impact, IsLoading: false })),
      tap(impact => cache.impacts.set(cacheKey, impact)),
      catchError(() => {
        const unavailableImpact = this.emptyImpact(false, false);
        cache.impacts.set(cacheKey, unavailableImpact);
        return of(unavailableImpact);
      }),
      finalize(() => cache.inFlight.delete(cacheKey)),
      startWith(this.loadingImpact()),
      publishReplay(1),
      refCount()
    );
    cache.inFlight.set(cacheKey, load$);
    return load$;
  }

  private createCacheKey(courseId: number, studentIds: number[]): string {
    return `${courseId}:${studentIds.slice().sort((first, second) => first - second).join(',')}`;
  }

  private mergeImpact(
    summary: AssignmentResetImpact,
    details: AssignmentResetImpact,
    request: AssignmentResetImpactRequest,
    loadingCourseLessonIds: number[] = []
  ): AssignmentResetImpact {
    const selectedLessonIds = new Set(request.CourseLessonIds);
    const detailsByLessonId = new Map<number, AssignmentResetLessonImpact>();
    (details?.Lessons || []).forEach(lesson => detailsByLessonId.set(lesson.CourseLessonId, lesson));
    const lessons = (summary.Lessons || []).map(lesson => ({
      ...lesson,
      ...(detailsByLessonId.get(lesson.CourseLessonId) || {}),
      Selected: selectedLessonIds.has(lesson.CourseLessonId)
    }));

    return {
      ...summary,
      ...(details || {}),
      IsLoading: false,
      IsRefreshing: false,
      IsAvailable: summary.IsAvailable && details?.IsAvailable !== false,
      EvaluatedStudentCount: summary.EvaluatedStudentCount,
      LoadingCourseLessonIds: loadingCourseLessonIds,
      ResetLessonProgress: request.ResetLessonProgress,
      WillResetProgress: !request.AssignmentId && request.ResetLessonProgress && details?.HasAffectedProgress === true,
      Lessons: lessons
    };
  }

  private createCachedDetails(
    courseLessonIds: number[],
    cache: AssignmentResetImpactCache
  ): AssignmentResetImpact {
    const lessons = courseLessonIds
      .map(lessonId => cache.lessonImpacts.get(lessonId))
      .filter((lesson): lesson is AssignmentResetLessonImpact => !!lesson);
    if (lessons.length === 0) {
      return null;
    }

    const students = new Map<number, AssignmentResetStudentImpact>();
    lessons.forEach(lesson => (lesson.Students || []).forEach(student => students.set(student.StudentId, student)));
    const modules: AssignmentResetModuleImpact[] = Array.from(new Set(lessons.map(lesson => lesson.CourseModuleId)))
      .filter(moduleId => moduleId > 0)
      .map(moduleId => {
        const moduleLessons = lessons.filter(lesson => lesson.CourseModuleId === moduleId);
        const moduleStudents = new Set<number>();
        moduleLessons.forEach(lesson => (lesson.Students || [])
          .forEach(student => moduleStudents.add(student.StudentId)));
        return {
          CourseModuleId: moduleId,
          StudentCount: moduleStudents.size || Math.max(...moduleLessons.map(lesson => lesson.StudentCount)),
          LessonCount: moduleLessons.filter(lesson => lesson.StudentCount > 0).length,
          StudentLessonCount: moduleLessons.reduce((sum, lesson) => sum + lesson.StudentCount, 0)
        };
      });
    return {
      ...this.emptyImpact(),
      HasAffectedProgress: lessons.some(lesson => lesson.StudentCount > 0),
      StudentCount: students.size || Math.max(...lessons.map(lesson => lesson.StudentCount)),
      ModuleCount: modules.length,
      LessonCount: lessons.filter(lesson => lesson.StudentCount > 0).length,
      StudentLessonCount: lessons.reduce((sum, lesson) => sum + lesson.StudentCount, 0),
      CompletedLessonCount: lessons.reduce((sum, lesson) => sum + lesson.CompletedStudentCount, 0),
      PassedQuizCount: lessons.reduce((sum, lesson) => sum + lesson.PassedQuizCount, 0),
      Students: Array.from(students.values()),
      Modules: modules,
      Lessons: lessons
    };
  }

  private loadingImpact(): AssignmentResetImpact {
    return { ...this.emptyImpact(), IsLoading: true };
  }

  private emptyImpact(resetLessonProgress: boolean = false, isAvailable: boolean = true): AssignmentResetImpact {
    return {
      IsLoading: false,
      IsRefreshing: false,
      IsAvailable: isAvailable,
      ResetLessonProgress: resetLessonProgress,
      HasAffectedProgress: false,
      WillResetProgress: false,
      EvaluatedStudentCount: 0,
      StudentCount: 0,
      ModuleCount: 0,
      LessonCount: 0,
      StudentLessonCount: 0,
      CompletedLessonCount: 0,
      PassedQuizCount: 0,
      LoadingCourseLessonIds: [],
      IncludesStudentsByLesson: false,
      Students: [],
      Modules: [],
      Lessons: []
    };
  }
}
